import { useState } from "react";
import { PhysShell, PHYS_ACCENT } from "./PhysShell.jsx";
import QuizMode from "../components/QuizMode.jsx";
import BSMPanel from "./BSMPanel.jsx";

// Standard Model elementary particles — values from PDG (Particle Data Group) 2024,
// supplemented by the V2 reference table (see SESSION-RESTORE 2026-05-20 / V42 Pass 1).
//
// SCHEMA CHANGES (V42 Pass 1, breaking from V41):
//   - `force: string` replaced by per-force booleans: strong/em/weak/gravity.
//     Gravity is true for ALL particles; surfacing it explicitly makes the
//     "four fundamental forces" pedagogy honest (it was silently dropped
//     before because nothing in the table carried it).
//   - New fields (all from V2 PDF, mostly displayed in Pass 2):
//       chirality   "L/R" | "L only" | null       (null = doesn't apply)
//       t3          string | null                 weak isospin (left-handed)
//       lifetime    string                        mean lifetime
//       self        boolean                       genuinely self-conjugate?
//       colors      string[] | null               for quarks ["r","g","b"]
//   - Fixed: muon-neutrino symbol νᵤ → νμ (corrected per V2 PDF).
//   - Added: graviton row, marked hypothetical (treated specially in Pass 2).
//
// UI BEHAVIOR (Pass 1): identical to V41. matches() now uses booleans for the
// force-* filters, but the chip ids are unchanged so the rendered filter row
// reads the same. The detail panel's "Interacts via" row recomposes the
// boolean fields into the same comma-joined string as before. No visible diff.

const PARTICLES = [
  // Quarks (fermions, spin 1/2). Feel all 4 forces. Each comes in r/g/b color states.
  { sym: "u", name: "Up quark",      cls: "quark", gen: 1, mass: "2.16 MeV/c²",   charge: "+2/3", spin: "1/2", strong: true,  em: true,  weak: true,  gravity: true, chirality: "L/R", t3: "+1/2 (L)", lifetime: "stable", self: false, colors: ["r","g","b"], anti: "anti-u" },
  { sym: "d", name: "Down quark",    cls: "quark", gen: 1, mass: "4.70 MeV/c²",   charge: "−1/3", spin: "1/2", strong: true,  em: true,  weak: true,  gravity: true, chirality: "L/R", t3: "−1/2 (L)", lifetime: "stable*", self: false, colors: ["r","g","b"], anti: "anti-d" },
  { sym: "c", name: "Charm quark",   cls: "quark", gen: 2, mass: "1.27 GeV/c²",   charge: "+2/3", spin: "1/2", strong: true,  em: true,  weak: true,  gravity: true, chirality: "L/R", t3: "+1/2 (L)", lifetime: "~10⁻¹² s", self: false, colors: ["r","g","b"], anti: "anti-c" },
  { sym: "s", name: "Strange quark", cls: "quark", gen: 2, mass: "93.5 MeV/c²",   charge: "−1/3", spin: "1/2", strong: true,  em: true,  weak: true,  gravity: true, chirality: "L/R", t3: "−1/2 (L)", lifetime: "~10⁻⁸ s", self: false, colors: ["r","g","b"], anti: "anti-s" },
  { sym: "t", name: "Top quark",     cls: "quark", gen: 3, mass: "172.57 GeV/c²", charge: "+2/3", spin: "1/2", strong: true,  em: true,  weak: true,  gravity: true, chirality: "L/R", t3: "+1/2 (L)", lifetime: "~10⁻²⁵ s", self: false, colors: ["r","g","b"], anti: "anti-t" },
  { sym: "b", name: "Bottom quark",  cls: "quark", gen: 3, mass: "4.18 GeV/c²",   charge: "−1/3", spin: "1/2", strong: true,  em: true,  weak: true,  gravity: true, chirality: "L/R", t3: "−1/2 (L)", lifetime: "~10⁻¹² s", self: false, colors: ["r","g","b"], anti: "anti-b" },

  // Charged leptons (fermions, spin 1/2). EM + weak + gravity.
  { sym: "e⁻",  symAscii: "e-",   name: "Electron",          cls: "lepton", gen: 1, mass: "0.511 MeV/c²",    charge: "−1", spin: "1/2", strong: false, em: true, weak: true, gravity: true, chirality: "L/R",    t3: "−1/2 (L)", lifetime: "stable",       self: false, colors: null, anti: "e+" },
  { sym: "νₑ",  symAscii: "ve",   name: "Electron neutrino", cls: "lepton", gen: 1, mass: "< 1 eV/c²",        charge: "0",  spin: "1/2", strong: false, em: false, weak: true, gravity: true, chirality: "L only", t3: "+1/2 (L)", lifetime: "stable*",      self: false, colors: null, anti: "anti-ve" },
  { sym: "μ⁻",  symAscii: "mu-",  name: "Muon",              cls: "lepton", gen: 2, mass: "105.66 MeV/c²",   charge: "−1", spin: "1/2", strong: false, em: true, weak: true, gravity: true, chirality: "L/R",    t3: "−1/2 (L)", lifetime: "~2.2 µs",      self: false, colors: null, anti: "mu+" },
  { sym: "νμ",  symAscii: "v-mu", name: "Muon neutrino",     cls: "lepton", gen: 2, mass: "< 0.19 MeV/c²",    charge: "0",  spin: "1/2", strong: false, em: false, weak: true, gravity: true, chirality: "L only", t3: "+1/2 (L)", lifetime: "stable*",      self: false, colors: null, anti: "anti-v-mu" },
  { sym: "τ⁻",  symAscii: "tau-", name: "Tau",               cls: "lepton", gen: 3, mass: "1776.86 MeV/c²",  charge: "−1", spin: "1/2", strong: false, em: true, weak: true, gravity: true, chirality: "L/R",    t3: "−1/2 (L)", lifetime: "~2.9×10⁻¹³ s", self: false, colors: null, anti: "tau+" },
  { sym: "ντ",  symAscii: "v-tau",name: "Tau neutrino",      cls: "lepton", gen: 3, mass: "< 18.2 MeV/c²",    charge: "0",  spin: "1/2", strong: false, em: false, weak: true, gravity: true, chirality: "L only", t3: "+1/2 (L)", lifetime: "stable*",      self: false, colors: null, anti: "anti-v-tau" },

  // Gauge bosons (spin 1). No generation. No chirality (T3 fixed per particle).
  { sym: "γ",   name: "Photon",   cls: "boson", gen: 0, mass: "0",            charge: "0",  spin: "1", strong: false, em: false, weak: false, gravity: true, chirality: null, t3: "0",  lifetime: "stable",     self: true,  colors: null, anti: "self" },
  // Gluon's "self" is a textbook simplification — the 8-gluon octet maps to
  // itself under color conjugation, but individual gluons like rḡ have a
  // distinct conjugate gr̄. We mark self:false and surface the nuance in
  // Pass 2's panel. Lifetime "confined" — never observed free.
  { sym: "g",   name: "Gluon",    cls: "boson", gen: 0, mass: "0",            charge: "0",  spin: "1", strong: true,  em: false, weak: false, gravity: true, chirality: null, t3: "0",  lifetime: "confined",   self: false, colors: ["rḡ","rb̄","gr̄","gb̄","br̄","bḡ","+2 mixed"], anti: "octet" },
  { sym: "Z⁰",  symAscii: "Z0",   name: "Z boson", cls: "boson", gen: 0, mass: "91.19 GeV/c²", charge: "0",  spin: "1", strong: false, em: false, weak: true, gravity: true, chirality: null, t3: "0",  lifetime: "~3×10⁻²⁵ s", self: true,  colors: null, anti: "self" },
  { sym: "W⁺",  symAscii: "W+",   name: "W boson", cls: "boson", gen: 0, mass: "80.37 GeV/c²", charge: "+1", spin: "1", strong: false, em: true, weak: true, gravity: true, chirality: null, t3: "+1", lifetime: "~3×10⁻²⁵ s", self: false, colors: null, anti: "W-" },

  // Scalar boson (spin 0). Higgs T3 = ±1/2 doublet; physical H0 is the
  // T3 = −1/2 component after electroweak symmetry breaking. The "self" flag
  // applies to the physical neutral scalar.
  { sym: "H⁰",  symAscii: "H0",   name: "Higgs boson", cls: "higgs", gen: 0, mass: "125.20 GeV/c²", charge: "0", spin: "0", strong: false, em: false, weak: true, gravity: true, chirality: null, t3: "±1/2 †", lifetime: "~1.6×10⁻²² s", self: true, colors: null, anti: "self" },

  // Hypothetical — graviton. Required by quantum gravity but never observed.
  // Marked cls="hypothetical" so Pass 2 can style it distinctly (dashed border,
  // tag) and Pass 1's existing filter behavior treats it as a 4th class.
  { sym: "G*",  symAscii: "G",    name: "Graviton",   cls: "hypothetical", gen: 0, mass: "0 (predicted)", charge: "0", spin: "2", strong: false, em: false, weak: false, gravity: true, chirality: null, t3: "0", lifetime: "stable", self: true, colors: null, anti: "self", hypothetical: true },
];

const CLS_COLOR = {
  quark:  "#7CD6FF",
  lepton: "#FFB37C",
  boson:  "#C9A6FF",
  higgs:  "#FFD56B",
  // Hypothetical (graviton) — muted, distinct from confirmed classes.
  hypothetical: "#6B7280",
};
const CLS_LABEL = {
  quark:  "Quarks",
  lepton: "Leptons",
  boson:  "Gauge bosons",
  higgs:  "Scalar boson",
  hypothetical: "Hypothetical",
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "quark", label: "Quarks" },
  { id: "lepton", label: "Leptons" },
  { id: "boson", label: "Bosons" },
  { id: "fermion", label: "Fermions" },
  { id: "gen-1", label: "Gen I" },
  { id: "gen-2", label: "Gen II" },
  { id: "gen-3", label: "Gen III" },
  { id: "force-strong", label: "Strong" },
  { id: "force-weak", label: "Weak" },
  { id: "force-EM", label: "EM" },
];

// Filter dispatch — Pass 1 uses the new per-force booleans for force-*.
// Pass 3 adds gen-* filters. The class filters are unchanged. The fermion
// filter is unchanged (quark || lepton). "hypothetical" is intentionally
// not in any class/gen/force filter; it appears only under "all" (Pass 2
// added dedicated panel styling to keep BSM/hypothetical visually distinct;
// Pass 4 will add a dedicated BSM info panel for the broader BSM zoo).
function matches(p, f) {
  if (p.cls === "hypothetical" && f !== "all") return false;
  if (f === "all") return true;
  if (f === "quark" || f === "lepton" || f === "boson" || f === "higgs") return p.cls === f;
  if (f === "fermion") return p.cls === "quark" || p.cls === "lepton";
  if (f === "gen-1")        return p.gen === 1;
  if (f === "gen-2")        return p.gen === 2;
  if (f === "gen-3")        return p.gen === 3;
  if (f === "force-strong") return p.strong === true;
  if (f === "force-weak")   return p.weak === true;
  if (f === "force-EM")     return p.em === true;
  return true;
}

// Compose the boolean force fields into the same display string V41 produced,
// so the detail panel's "Interacts via" row reads identically. Adds gravity
// only for the graviton (otherwise omitted — gravity is universal but listing
// it on every row is noise; surfaced in Pass 2 as a dedicated row instead).
function forceString(p) {
  const f = [];
  if (p.strong) f.push("strong");
  if (p.em)     f.push("EM");
  if (p.weak)   f.push("weak");
  if (f.length === 3) return "strong, EM, weak"; // matches V41's "all" rendering
  if (f.length === 0) return p.gravity ? "gravity only" : "—";
  return f.join(", ");
}

export function PhysParticles() {
  const [filter, setFilter] = useState("all");
  const [showAnti, setShowAnti] = useState(false);
  const [sel, setSel] = useState(null);
  const [quiz, setQuiz] = useState(false);
  // V42 Pass 4 — BSM info panel (separate from the main grid).
  const [showBSM, setShowBSM] = useState(false);

  const shown = PARTICLES.filter((p) => matches(p, filter));

  // Curated recall over the Standard Model data already in this tool
  // (NOT AI generation — see QuizMode header). Prompt = particle name,
  // answer = its key properties. Hypothetical particles excluded from
  // the quiz (kid shouldn't be quizzed on graviton properties as if
  // they were confirmed).
  const quizCards = PARTICLES.filter((p) => p.cls !== "hypothetical").map((p) => ({
    q: p.name,
    a: `Symbol: ${p.sym} · Class: ${CLS_LABEL[p.cls]} · Charge: ${p.charge} · Spin: ${p.spin} · Mass: ${p.mass}`,
    ctx: forceString(p),
  }));

  return (
    <PhysShell>
      {quiz ? (
        <QuizMode title="Particles · Quiz" cards={quizCards} onExit={() => setQuiz(false)} />
      ) : (
      <>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "#E8E9F3", margin: "0 0 4px" }}>
        Elementary particles
      </h2>
      <p style={{ fontSize: 13.5, color: "#9A9DBE", margin: "0 0 18px" }}>
        The Standard Model in one place. Tap any particle for its properties.
      </p>

      <button
        onClick={() => setQuiz(true)}
        style={{
          marginBottom: 16, padding: "9px 14px", borderRadius: 10, border: "none",
          background: PHYS_ACCENT, color: "#06121C", fontWeight: 700, fontSize: 13,
          cursor: "pointer",
        }}
      >
        Quiz me
      </button>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              padding: "6px 11px",
              borderRadius: 8,
              border: `1px solid ${filter === f.id ? PHYS_ACCENT : "#1C1E2E"}`,
              background: filter === f.id ? PHYS_ACCENT + "22" : "transparent",
              color: filter === f.id ? PHYS_ACCENT : "#9A9DBE",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={() => setShowAnti((v) => !v)}
          style={{
            padding: "6px 11px",
            borderRadius: 8,
            border: `1px solid ${showAnti ? PHYS_ACCENT : "#1C1E2E"}`,
            background: showAnti ? PHYS_ACCENT + "22" : "transparent",
            color: showAnti ? PHYS_ACCENT : "#9A9DBE",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {showAnti ? "Showing antiparticles" : "Show antiparticles"}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
          gap: 10,
        }}
      >
        {shown.map((p) => {
          const c = CLS_COLOR[p.cls];
          const showingAnti = showAnti && p.anti !== "self" && p.anti !== "octet";
          return (
            <button
              key={p.sym}
              onClick={() => {
                if (showingAnti) {
                  // Tapped an antiparticle — show ITS identity, not the
                  // particle's. Charge flips sign; name uses the proper
                  // term where one exists.
                  const PROPER = {
                    Electron: "Positron",
                    "Up quark": "Up antiquark",
                    "Down quark": "Down antiquark",
                    "Charm quark": "Charm antiquark",
                    "Strange quark": "Strange antiquark",
                    "Top quark": "Top antiquark",
                    "Bottom quark": "Bottom antiquark",
                  };
                  const flipCharge = (c) =>
                    c === "0" ? "0" : c.startsWith("−") ? "+" + c.slice(1) : "−" + c.replace("+", "");
                  setSel({
                    ...p,
                    sym: p.anti,
                    symAscii: null,
                    name: PROPER[p.name] || (p.name.startsWith("anti") ? p.name : "Anti" + p.name.toLowerCase()),
                    charge: flipCharge(p.charge),
                    anti: p.name,
                    _isAnti: true,
                  });
                } else {
                  setSel(p);
                }
              }}
              style={{
                aspectRatio: "1 / 1",
                background: "transparent",
                // Hypothetical particles (graviton) — dashed border to read
                // distinct from confirmed-SM cells at a glance.
                border: p.hypothetical ? `1px dashed ${c}88` : `1px solid ${c}55`,
                borderRadius: 12,
                color: "#E8E9F3",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                padding: 6,
              }}
            >
              <span style={{ fontSize: 22, fontWeight: 700, color: c }}>
                {showingAnti ? p.anti : p.sym}
              </span>
              <span style={{ fontSize: 10, color: "#9A9DBE", textAlign: "center", lineHeight: 1.2 }}>
                {showingAnti ? "anti-" + p.name.toLowerCase() : p.name}
              </span>
            </button>
          );
        })}
      </div>

      {shown.length === 0 && (
        <p style={{ fontSize: 13, color: "#5B5E7A", marginTop: 16 }}>No particles match that filter.</p>
      )}

      {/* V42 Pass 4 — BSM info panel opener. Deliberately separate from the
          main filter row: BSM particles are HYPOTHESES, not confirmed
          physics, and putting them in any filter would let a kid pick
          "WIMP" alongside "Electron" as if they're equivalent. The button
          here, with its dashed border echoing the hypothetical visual
          language from the main grid, signals "different category of
          information". */}
      <button
        onClick={() => setShowBSM(true)}
        style={{
          marginTop: 28, padding: "10px 16px", borderRadius: 10,
          border: "1px dashed #6B7280",
          background: "transparent",
          color: "#9A9DBE",
          fontFamily: "inherit",
          fontSize: 13, fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Beyond the Standard Model →
      </button>

      {showBSM && <BSMPanel onClose={() => setShowBSM(false)} />}

      {sel && (
        <div
          onClick={() => setSel(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#13141F",
              // Hypothetical particles get a DASHED border to read visually
              // distinct from confirmed-SM particles — the kid sees the
              // difference at a glance, without prose having to spell it out.
              border: sel.hypothetical
                ? `1px dashed ${CLS_COLOR[sel.cls]}99`
                : `1px solid ${CLS_COLOR[sel.cls]}55`,
              borderRadius: 16,
              padding: 24,
              maxWidth: 340,
              width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 38, fontWeight: 800, color: CLS_COLOR[sel.cls] }}>{sel.sym}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{sel.name}</div>
                  {sel.hypothetical && (
                    <span style={{
                      fontSize: 9.5, fontWeight: 700, letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      color: "#FBBF24",
                      background: "rgba(251, 191, 36, 0.12)",
                      border: "1px solid rgba(251, 191, 36, 0.4)",
                      padding: "2px 6px", borderRadius: 4,
                    }}>
                      Hypothetical
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#9A9DBE" }}>{CLS_LABEL[sel.cls]}{sel.symAscii ? "  ·  " + sel.symAscii : ""}</div>
              </div>
            </div>
            {[
              ["Mass", sel.mass],
              ["Electric charge", sel.charge + " e"],
              ["Spin", sel.spin + " ℏ"],
              ["Interacts via", forceString(sel)],
              ["Gravity", sel.gravity ? "yes (universal)" : "no"],
              ["Generation", sel.gen === 0 ? "—" : sel.gen],
              // Chirality — null for bosons (doesn't apply); otherwise show
              // the kid the deepest weak-force asymmetry in the Standard Model.
              sel.chirality && ["Chirality", sel.chirality === "L only" ? "left-handed only" : "left & right"],
              // Weak isospin (T₃) — left-handed-state value per the V2 PDF.
              sel.t3 && ["Weak isospin (T₃)", sel.t3],
              ["Lifetime", sel.lifetime],
              // Self-conjugate — keep simple yes/no but the gluon's "octet"
              // anti gets a clearer rendering than just "no".
              ["Self-conjugate?", sel.self ? "yes" : sel.anti === "octet" ? "octet maps to itself" : "no"],
              // Color states — quarks/gluons only. List for quarks; for
              // gluons it's the 8-octet combinations.
              sel.colors && [sel.cls === "quark" ? "Color states" : "Color combinations", sel.colors.join(", ")],
              ["Antiparticle", sel.anti === "self" ? "its own antiparticle" : sel.anti === "octet" ? "octet (see notes)" : sel.anti],
            ].filter(Boolean).map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 0",
                  borderBottom: "1px solid #1C1E2E",
                  fontSize: 13.5,
                }}
              >
                <span style={{ color: "#9A9DBE" }}>{k}</span>
                <span style={{ fontWeight: 600 }}>{v}</span>
              </div>
            ))}
            {sel.cls === "lepton" && sel.charge === "0" && (
              <p style={{ fontSize: 11.5, color: "#5B5E7A", margin: "12px 0 0", lineHeight: 1.4 }}>
                Neutrinos have tiny nonzero mass. Only upper limits and mass-squared
                differences are known; the value shown is a loose direct-kinematic limit.
              </p>
            )}
            {sel.hypothetical && (
              <p style={{ fontSize: 11.5, color: "#FBBF24", margin: "12px 0 0", lineHeight: 1.4 }}>
                Predicted but never observed. The graviton is the hypothetical carrier
                of gravity — required to reconcile general relativity with quantum
                field theory at high energies. Listed here to complete the
                "four fundamental forces" picture, not as confirmed physics.
              </p>
            )}
            <button
              onClick={() => setSel(null)}
              style={{
                marginTop: 18,
                width: "100%",
                padding: "11px",
                borderRadius: 10,
                border: "none",
                background: PHYS_ACCENT,
                color: "#06121C",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
      </>
      )}
    </PhysShell>
  );
}

export default PhysParticles;
